/**
 * Console Logger Utility
 *
 * Captures every browser console output (log, info, warn, error, debug),
 * uncaught errors, and unhandled promise rejections, then streams them in
 * real-time to MongoDB so we have a complete record of every user session.
 *
 * Key behaviors:
 *   - Buffers logs and flushes every 5s OR when buffer hits 25 entries.
 *   - Final flush on page unload uses navigator.sendBeacon() so the browser
 *     guarantees delivery even after the tab closes.
 *   - One sessionId per page load (persisted in sessionStorage so reloads
 *     keep the same id).
 *   - setProfileContext() ties subsequent logs to the audited profile + email.
 *   - In-memory buffer still capped at 1000 entries for getLogs() backward
 *     compatibility (used by AI service summary).
 *   - Logger's own POSTs are NOT captured to avoid feedback loops.
 */

const STREAM_ENDPOINT = process.env.NODE_ENV === 'production'
  ? '/api/log-stream'
  : 'http://localhost:3001/api/log-stream';

const FLUSH_INTERVAL_MS = 5000;     // flush every 5 seconds
const FLUSH_BATCH_SIZE  = 25;       // or when 25+ pending logs
const MAX_BATCH_BYTES   = 60 * 1024; // safety: ~60KB per POST
const MEMORY_CAP        = 1000;     // in-memory cap for getLogs()
const SESSION_KEY       = 'lpo_logger_session_id';

function generateSessionId() {
  // Best-effort UUID. crypto.randomUUID is widely available in modern browsers.
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) { /* fall through */ }
  return 'sess_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
}

function getOrCreateSessionId() {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const fresh = generateSessionId();
    sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch (_) {
    // sessionStorage may be disabled (private mode, iframes)
    return generateSessionId();
  }
}

function safeStringify(arg) {
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error) return `${arg.message}\n${arg.stack || ''}`;
  if (arg === null || arg === undefined) return String(arg);
  if (typeof arg === 'function') return `[Function ${arg.name || 'anonymous'}]`;
  try {
    // Strip circular refs and cap object depth implicitly via JSON.stringify
    return JSON.stringify(arg, (k, v) => {
      // Drop common sensitive keys defensively. Server-side validation also
      // strips these, but front-end stripping reduces network exposure.
      if (typeof k === 'string' && /password|token|secret|api[_-]?key|authorization/i.test(k)) {
        return '[redacted]';
      }
      return v;
    });
  } catch {
    try { return String(arg); } catch { return '[unserializable]'; }
  }
}

class ConsoleLogger {
  constructor() {
    this.logs = [];                // in-memory buffer for getLogs()
    this.maxLogs = MEMORY_CAP;
    this.isCapturing = false;
    this.originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info,
      debug: console.debug,
    };
    // Streaming state
    this.sessionId = null;
    this.pending = [];             // logs queued for next flush
    this.flushTimer = null;
    this.profileContext = { profileUrl: null, userEmail: null };
    this.streamingEnabled = true;
    this.inFlightFlush = false;
    // Mark logger-internal POSTs so we don't recursively log them
    this._suppressNextFetch = false;
  }

  /**
   * Start capturing console output AND streaming to backend.
   */
  startCapture() {
    if (this.isCapturing) return;
    this.isCapturing = true;
    this.sessionId = getOrCreateSessionId();
    const self = this;

    const wrap = (level, original) => function (...args) {
      self._addLog(level, args);
      original.apply(console, args);
    };

    console.log   = wrap('LOG',   this.originalConsole.log);
    console.warn  = wrap('WARN',  this.originalConsole.warn);
    console.error = wrap('ERROR', this.originalConsole.error);
    console.info  = wrap('INFO',  this.originalConsole.info);
    console.debug = wrap('DEBUG', this.originalConsole.debug);

    // Uncaught errors (script errors, render exceptions)
    window.addEventListener('error', (event) => {
      self._addLog('UNCAUGHT_ERROR', [
        `${event.message} at ${event.filename}:${event.lineno}:${event.colno}`,
        event.error?.stack,
      ]);
    });

    // Unhandled promise rejections (async failures)
    window.addEventListener('unhandledrejection', (event) => {
      self._addLog('UNHANDLED_REJECTION', [
        event.reason instanceof Error
          ? `${event.reason.message}\n${event.reason.stack || ''}`
          : event.reason,
      ]);
    });

    // Streaming: periodic flush
    this.flushTimer = setInterval(() => self._flush('interval'), FLUSH_INTERVAL_MS);

    // Streaming: final flush on tab close. pagehide is more reliable than
    // beforeunload on mobile and bfcache scenarios.
    const finalFlush = () => self._flushBeacon();
    window.addEventListener('pagehide', finalFlush);
    window.addEventListener('beforeunload', finalFlush);

    console.log('🔍 [LOGGER] Console capture + streaming started, session=' + this.sessionId);
  }

  /**
   * Tie subsequent logs to a specific profile/user. Safe to call multiple times.
   */
  setProfileContext({ profileUrl, userEmail } = {}) {
    if (profileUrl) this.profileContext.profileUrl = profileUrl;
    if (userEmail)  this.profileContext.userEmail  = userEmail;
  }

  /**
   * Force an immediate flush. Useful after critical events (audit done,
   * payment success/fail, error).
   */
  flush() {
    return this._flush('manual');
  }

  /**
   * Add a log entry to both in-memory buffer and the pending stream queue.
   */
  _addLog(type, args) {
    try {
      const ts = new Date().toISOString();
      const message = args.map(safeStringify).join(' ');
      const entry = { timestamp: ts, type, message };

      // In-memory buffer (rolling cap)
      this.logs.push(entry);
      if (this.logs.length > this.maxLogs) this.logs.shift();

      // Stream queue
      if (this.streamingEnabled) {
        this.pending.push(entry);
        if (this.pending.length >= FLUSH_BATCH_SIZE) {
          this._flush('batch_full');
        }
      }
    } catch (err) {
      // Never let logger errors break the app or recurse
      this.originalConsole.error('Logger error:', err);
    }
  }

  /**
   * Flush via standard fetch. Returns immediately if already in flight or empty.
   */
  async _flush(reason) {
    if (this.inFlightFlush) return;
    if (!this.pending.length) return;

    // Move pending to a local batch so new logs can queue while we POST
    const batch = this.pending.splice(0, this.pending.length);
    this.inFlightFlush = true;
    this._suppressNextFetch = true;

    const payload = {
      sessionId: this.sessionId,
      profileUrl: this.profileContext.profileUrl,
      userEmail:  this.profileContext.userEmail,
      reason,
      events: batch,
      // Useful debug context server-side
      pageUrl: typeof window !== 'undefined' ? window.location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    };

    try {
      // Cap payload size defensively. If too big, drop oldest in batch.
      const json = JSON.stringify(payload);
      const bodyStr = json.length > MAX_BATCH_BYTES
        ? JSON.stringify({ ...payload, events: batch.slice(-Math.floor(FLUSH_BATCH_SIZE / 2)) })
        : json;

      await fetch(STREAM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: bodyStr,
        keepalive: true,
        credentials: 'omit',
      }).catch(() => {
        // Re-queue on network failure so next flush retries.
        // Cap re-queued size so we don't grow forever during long outages.
        const headroom = Math.max(0, MEMORY_CAP - this.pending.length);
        this.pending.unshift(...batch.slice(0, headroom));
      });
    } finally {
      this.inFlightFlush = false;
      this._suppressNextFetch = false;
    }
  }

  /**
   * Final flush via sendBeacon. Browser guarantees delivery even after the
   * tab closes. Synchronous, fire-and-forget.
   */
  _flushBeacon() {
    if (!this.pending.length) return;
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) return;

    const batch = this.pending.splice(0, this.pending.length);
    const payload = JSON.stringify({
      sessionId: this.sessionId,
      profileUrl: this.profileContext.profileUrl,
      userEmail:  this.profileContext.userEmail,
      reason: 'pagehide',
      events: batch,
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
    });

    try {
      navigator.sendBeacon(STREAM_ENDPOINT, new Blob([payload], { type: 'application/json' }));
    } catch (_) { /* swallow, page is closing */ }
  }

  /**
   * Backward-compat: AI service still calls getLogs() to embed a snapshot
   * in the AI analysis save. Returns formatted string.
   */
  getLogs() {
    return this.logs.map(log =>
      `[${log.timestamp}] [${log.type}] ${log.message}`
    ).join('\n');
  }

  getLogsArray() {
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
  }

  stopCapture() {
    if (!this.isCapturing) return;
    // Final flush before tearing down
    this._flush('stop').catch(() => {});

    console.log = this.originalConsole.log;
    console.warn = this.originalConsole.warn;
    console.error = this.originalConsole.error;
    console.info = this.originalConsole.info;
    console.debug = this.originalConsole.debug;

    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.isCapturing = false;
    this.originalConsole.log('🔍 [LOGGER] Console capture stopped');
  }

  getStats() {
    const stats = { total: this.logs.length, byType: {}, sessionId: this.sessionId };
    this.logs.forEach(log => {
      stats.byType[log.type] = (stats.byType[log.type] || 0) + 1;
    });
    return stats;
  }
}

const consoleLogger = new ConsoleLogger();
export default consoleLogger;
